import type { CaptureResult, Properties } from 'posthog-js'

export const PUBLIC_CV_PATH = /^\/cv(\/|$)/i

export const OAUTH_CONSENT_PATH = /^\/app\/oauth\/consent(\/|$)/i

export const TRY_CONFIRM_PATH = /^\/(?:(?:fr|es|pt)\/)?try\/confirm(\/|$)/i

export const WORKSPACE_INVITE_PATH = /^\/app\/workspace\/invite(\/|$)/i

export const REFEREE_LINK_PATH = /^\/recommend(\/|$)/i

export const SURVEY_PARTICIPANT_PATH = /^\/(feedback|f)\//i

export const PERSONALITY_REQUEST_PATH = /^\/(app\/)?auth\/personality-request(\/|$)/i

export const REDACTED_PUBLIC_CV_PATH = '/cv/:token'

export const REDACTED_OAUTH_CONSENT_PATH = '/app/oauth/consent'

export const REDACTED_TRY_CONFIRM_PATH = '/try/confirm/:token'

export const REDACTED_WORKSPACE_INVITE_PATH = '/app/workspace/invite/:token'

export const REDACTED_REFEREE_LINK_PATH = '/recommend/:token'

export const REDACTED_SURVEY_PATH = '/feedback/:token'

export const REDACTED_PERSONALITY_REQUEST_PATH = '/auth/personality-request'

function pathnameOf(path: string): string {
  return path.split(/[?#]/)[0] ?? path
}

export function isPublicCVPath(path: string): boolean {
  return PUBLIC_CV_PATH.test(pathnameOf(path))
}

export function isRefereeLinkPath(path: string): boolean {
  return REFEREE_LINK_PATH.test(pathnameOf(path))
}

export function isSensitiveURLPath(path: string): boolean {
  const pathname = pathnameOf(path)
  return (
    PUBLIC_CV_PATH.test(pathname) ||
    OAUTH_CONSENT_PATH.test(pathname) ||
    TRY_CONFIRM_PATH.test(pathname) ||
    WORKSPACE_INVITE_PATH.test(pathname) ||
    REFEREE_LINK_PATH.test(pathname) ||
    SURVEY_PARTICIPANT_PATH.test(pathname) ||
    PERSONALITY_REQUEST_PATH.test(pathname)
  )
}

export function isSensitiveLocation(pathname: string, search: string): boolean {
  return isSensitiveURLPath(pathname) || new URLSearchParams(search).has('invite_token')
}

export function redactPublicCVPath(path: string): string {
  return isPublicCVPath(path) ? REDACTED_PUBLIC_CV_PATH : path
}

export function redactSensitiveURLPath(path: string): string {
  const pathname = pathnameOf(path)
  if (PUBLIC_CV_PATH.test(pathname)) {
    return REDACTED_PUBLIC_CV_PATH
  }
  if (OAUTH_CONSENT_PATH.test(pathname)) {
    return REDACTED_OAUTH_CONSENT_PATH
  }
  if (TRY_CONFIRM_PATH.test(pathname)) {
    return REDACTED_TRY_CONFIRM_PATH
  }
  if (WORKSPACE_INVITE_PATH.test(pathname)) {
    return REDACTED_WORKSPACE_INVITE_PATH
  }
  if (REFEREE_LINK_PATH.test(pathname)) {
    return REDACTED_REFEREE_LINK_PATH
  }
  if (SURVEY_PARTICIPANT_PATH.test(pathname)) {
    return REDACTED_SURVEY_PATH
  }
  if (PERSONALITY_REQUEST_PATH.test(pathname)) {
    return REDACTED_PERSONALITY_REQUEST_PATH
  }
  return path
}

const INVITE_SEGMENT = /\/app\/workspace\/invite\/[^/?#]+/gi

const INVITE_MARKER = /invite(?:_|%5f)token|workspace%2finvite|\/app\/workspace\/invite(\/|$|[?#])/i

function hasInviteTokenParam(value: string): boolean {
  try {
    return new URL(value, 'https://peatch.invalid').searchParams.has('invite_token')
  } catch {
    return false
  }
}

export function sanitizeInviteURL(value: string): string {
  if (!INVITE_MARKER.test(value) && !hasInviteTokenParam(value)) {
    return value
  }
  return pathnameOf(value).replace(INVITE_SEGMENT, '/app/workspace/invite/:token')
}

const REFEREE_LINK_SEGMENT = /\/recommend\/[^/?#]+/gi

const SURVEY_PARTICIPANT_SEGMENT = /^((?:[a-z][a-z0-9+.-]*:\/\/[^/?#]*)?)\/(?:feedback|f)\/[^/?#]+/i

function sanitizeURLProperty(value: string): string {
  return sanitizeInviteURL(value)
    .replace(REFEREE_LINK_SEGMENT, REDACTED_REFEREE_LINK_PATH)
    .replace(SURVEY_PARTICIPANT_SEGMENT, `$1${REDACTED_SURVEY_PATH}`)
}

const URL_PROPERTY_KEY = /(?:^\$|_)(?:url|referrer|pathname)$/i

function sanitizeURLProperties(properties: Properties | undefined): void {
  if (!properties) {
    return
  }
  for (const [key, value] of Object.entries(properties)) {
    if (typeof value === 'string' && URL_PROPERTY_KEY.test(key)) {
      properties[key] = sanitizeURLProperty(value)
    }
  }
}

export function sanitizeCapturedEvent(event: CaptureResult | null): CaptureResult | null {
  if (!event) {
    return event
  }
  sanitizeURLProperties(event.properties)
  sanitizeURLProperties(event.$set)
  sanitizeURLProperties(event.$set_once)
  return event
}
